import { FitterDay } from '../board/board-geometry';
/** A block on the scheduler (GET schedule-block). Times are UK wall-clock 'YYYY-MM-DD HH:mm'. */
export interface ScheduleBlock {
  id: number;
  fitterId: number;
  fitterName: string;
  jobType: 'defect' | 'inspection';
  workshopDefectId: number | null;
  vehicleInspectionId: number | null;
  status: 'planned' | 'done';
  actualHours: number | null;
  notes: string | null;
  startAt: string;
  endAt: string;
  registration: string;
  title: string;
  colour: string;
}

/** Something waiting to be put on the scheduler (GET schedule-block/to-schedule). */
export interface JobToSchedule {
  id: number;
  jobType: 'defect' | 'inspection';
  registration: string;
  title: string;
  status: string | null;
  minutes: number;
  colour: string;
  dueDate: string | null;
}

export interface SchedulerFitter {
  id: number;
  name: string;
  active: boolean;
}

/** `hours[fitterId][YYYY-MM-DD]`: that fitter's working windows that day (see board-geometry FitterDay). */
export interface SchedulerFittersResponse {
  fitters: SchedulerFitter[];
  absences: FitterAbsence[];
  hours: Record<number, Record<string, FitterDay>>;
}

export interface FitterAbsence {
  id: number;
  fitterId: number;
  startDate: string;
  endDate: string;
  typeName: string | null;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD HH:mm' → Date at that local (UK) wall-clock time. */
export function fromWallClock(value: string): Date {
  const [d, t = '00:00'] = value.split(' ');
  const [y, m, day] = d.split('-').map(Number);
  const [h, mi] = t.split(':').map(Number);
  return new Date(y, m - 1, day, h, mi);
}

/** Date → 'YYYY-MM-DD HH:mm' in local (UK) wall-clock time. */
export function toWallClock(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Date → 'YYYY-MM-DD' (local). */
export function toIsoDay(date: Date): string {
  return toWallClock(date).slice(0, 10);
}

/** Rounds to the nearest 15 minutes. */
export function snap15(date: Date): Date {
  const ms = 15 * 60 * 1000;
  return new Date(Math.round(date.getTime() / ms) * ms);
}

/** 90 → '1h30', 60 → '1h', 45 → '45m'. */
export function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h ? `${h}h${m ? pad(m) : ''}` : `${m}m`;
}

export function escapeHtml(text: string): string {
  return String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as any)[c]);
}
